import {ButtonGroup, Card, DataStatus, IconButton, openDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {isNil} from 'ramda';

import {
  RecordAuditSeriesDefinitionResponseBody,
  useGetBranchListQuery,
  useGetRecordAuditSeriesDefinitionListQuery,
  useGetSeriesListQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {SimpleTable} from '@dms/teas';

import {RECORD_AUDIT_SERIES_TYPE} from '../constants/RECORD_AUDIT_SERIES_TYPE';
import {RecordAuditSeriesDefinitionForm} from './RecordAuditSeriesDefinitionForm';

const FORM_TEST_ID = 'recordAuditSeriesDefinitionForm';
const ACTIONS_COLUMN_WIDTH = 100;

export function RecordAuditSeriesDefinitionList() {
  const {
    data: definitions = [],
    isLoading: isLoadingDefinitions,
    isError: isErrorDefinitions,
  } = useGetRecordAuditSeriesDefinitionListQuery();

  const {data: branches} = useGetBranchListQuery();
  const {data: series} = useGetSeriesListQuery({
    type: [RECORD_AUDIT_SERIES_TYPE],
  });

  // One definition per branch: nothing to create once every branch has one.
  const definedBranchIds = definitions.map((definition) => definition.branchId);
  const hasBranchWithoutDefinition =
    branches?.branchListItems.some((branch) => !definedBranchIds.includes(branch.id)) ?? false;

  const handleOpenEdit = (definitionId: RecordAuditSeriesDefinitionResponseBody['id']) => {
    openDialog(
      <RecordAuditSeriesDefinitionForm definitionId={definitionId} data-testid={FORM_TEST_ID} />,
      {
        title: i18n.t('page.settings.recordAuditSeriesDefinition.actions.editDefinition'),
      }
    );
  };

  const handleCreateNew = () => {
    openDialog(<RecordAuditSeriesDefinitionForm data-testid={FORM_TEST_ID} />, {
      title: i18n.t('page.settings.recordAuditSeriesDefinition.actions.newDefinition'),
    });
  };

  const columns = [
    {
      Header: i18n.t('entity.seriesDefinition.labels.definitionName'),
      id: 'name',
    },
    {
      Header: i18n.t('entity.branch.labels.branch'),
      id: 'branch',
      Cell: (cellProps: {row: RecordAuditSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        if (isNil(row.branchId)) {
          return i18n.t('general.labels.all');
        }

        return (
          branches?.branchListItems.find((branch) => branch.id === row.branchId)?.marketingName ??
          row.branchId
        );
      },
    },
    {
      Header: i18n.t('page.settings.recordAuditSeriesDefinition.labels.defaultSeries'),
      id: 'defaultSeries',
      Cell: (cellProps: {row: RecordAuditSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        return series?.find((item) => item.id === row.defaultSeriesId)?.name ?? row.defaultSeriesId;
      },
    },
    {
      Header: '',
      id: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      Cell: (cellProps: {row: RecordAuditSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        return (
          <Box paddingHorizontal={3}>
            <ButtonGroup align="right">
              <IconButton
                size="small"
                icon="image/edit"
                aria-label={i18n.t('general.actions.edit')}
                data-testid={`recordAuditSeriesDefinitionList-edit-${row.id}`}
                onClick={() => handleOpenEdit(row.id)}
              />
            </ButtonGroup>
          </Box>
        );
      },
    },
  ];

  return (
    <Card
      title={i18n.t('page.settings.recordAuditSeriesDefinition.labels.definitions')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'content/add_circle',
          onClick: handleCreateNew,
          isDisabled: !hasBranchWithoutDefinition,
          title: i18n.t('page.settings.recordAuditSeriesDefinition.actions.newDefinition'),
        },
      ]}
    >
      <DataStatus isLoading={isLoadingDefinitions} isError={isErrorDefinitions} spacing={5}>
        <SimpleTable
          noZebra
          showRowDivider
          tdPadding="default"
          highlightRowOnHover
          rows={[
            {
              data: definitions,
            },
          ]}
          getRowId={(row) => row.id}
          columns={columns}
        />
      </DataStatus>
    </Card>
  );
}
